/**
 * Pure Couverture (coverage) model for the Prompt Studio (lot B step 2).
 * No React, no API calls.
 *
 * @see docs/plans/prompt-studio-build/B-popup-frontend.md §Files and props coverageModel.ts
 */

import {
  coveragePlaceKey,
} from '@traaker/shared';
import type { BrandQuery, BrandQueryAxis, BrandStrategy, TargetZone } from '@traaker/shared';
import type { Location } from '@traaker/shared';
import { isClassified, MONDIAL, locKeyOf } from './promptStudioModel';
import type { Family, LocKey, CellTriple } from './promptStudioModel';

// ---------------------------------------------------------------------------
// Orientation
// ---------------------------------------------------------------------------

/**
 * §9.4 — the orientation margin.  0 = the plain design rule.
 * Only this constant may change.
 */
export const ORIENTATION_MARGIN = 0;

// ---------------------------------------------------------------------------
// vocabFor / nVocabMax / zonesAsRows
// ---------------------------------------------------------------------------

/**
 * Vocabulary for a given axis from the brand strategy.
 *   narrative + comparison → decisionCriteria ?? []
 *   authority              → keywords ?? []
 *   referral               → scenarios ?? []
 */
export function vocabFor(
  s: BrandStrategy | undefined,
  ax: BrandQueryAxis,
): string[] {
  if (!s) return [];
  if (ax === 'narrative' || ax === 'comparison') return s.decisionCriteria ?? [];
  if (ax === 'authority')  return s.keywords  ?? [];
  if (ax === 'referral')   return s.scenarios  ?? [];
  return [];
}

/**
 * MAX vocabulary length over the FOUR axes.
 * Comparaison's vocabulary is Image's, so the flip never changes the orientation.
 */
export function nVocabMax(s: BrandStrategy | undefined): number {
  if (!s) return 0;
  return Math.max(
    (s.decisionCriteria ?? []).length,
    (s.keywords  ?? []).length,
    (s.scenarios  ?? []).length,
  );
}

/**
 * True when zones (rows) is the orientation (zones as row headers,
 * vocabulary as column headers).
 */
export function zonesAsRows(nZones: number, nVocab: number): boolean {
  return nZones >= nVocab * (1 + ORIENTATION_MARGIN);
}

// ---------------------------------------------------------------------------
// termsOf
// ---------------------------------------------------------------------------

/**
 * Resolved vocabulary terms for a prompt row.
 *
 *   null  = UNCLASSIFIED (isClassified false), EXCEPT an authority/referral row
 *           with a legacy `topic` (it keeps its term).
 *   []    = (aucun) — classified but no terms found in vocab
 *   [...] = terms that intersect `vocab`
 */
export function termsOf(q: BrandQuery, vocab: string[]): string[] | null {
  // Exception: legacy topic on authority/referral counts as classified for terms
  if (!isClassified(q)) {
    if ((q.axis === 'authority' || q.axis === 'referral') && q.topic) {
      return [q.topic].filter((t) => vocab.includes(t));
    }
    return null;   // unclassified
  }

  if (q.axis === 'narrative' || q.axis === 'comparison') {
    const criteria = q.criteria ?? [];
    return criteria.filter((c) => vocab.includes(c));
  }
  if (q.axis === 'authority' || q.axis === 'referral') {
    const terms = q.topic ? [q.topic] : [];
    return terms.filter((t) => vocab.includes(t));
  }
  return [];
}

// ---------------------------------------------------------------------------
// unclassifiedCount
// ---------------------------------------------------------------------------

/**
 * Active prompts of the given axis whose termsOf is null (unclassified).
 * Counted on the Couverture foot line, excluded from every lane.
 */
export function unclassifiedCount(
  fs: Family[],
  ax: BrandQueryAxis,
  vocab: string[],
): number {
  let n = 0;
  for (const f of fs) {
    for (const q of f.rows) {
      if (q.status !== 'active') continue;
      if (q.axis !== ax) continue;
      if (termsOf(q, vocab) === null) n++;
    }
  }
  return n;
}

// ---------------------------------------------------------------------------
// coverageLocKey
// ---------------------------------------------------------------------------

/**
 * C1 R19 Couverture display-only loc key for a row.
 *
 * A row with no stored zoneMeta whose effective zone came from a Location
 * takes the promptPlaceKey of the ONE other known zone that lot A's C1 R19
 * matcher pairs with it (city with no regionName: equal zoneKeyFrom; region or
 * village: equal (cc, type, name part)), else locKeyOf(q).
 *
 * Couverture rows/columns only — families, filters, every save and the R3
 * no-op keep locKeyOf/placeKey (exact).
 */
export function coverageLocKey(
  q: BrandQuery,
  locations: readonly Location[],
  known: Map<LocKey, TargetZone>,
): LocKey {
  return coveragePlaceKey(q, locations, Array.from(known.values()));
}

// ---------------------------------------------------------------------------
// coverageLocs / covMembers
// ---------------------------------------------------------------------------

/**
 * Ordered list of loc keys for the Couverture table columns (or rows).
 * MONDIAL first, then distinct coverageLocKey values of active prompt rows.
 */
export function coverageLocs(
  fs: Family[],
  _sessionLocs: LocKey[],
  locations: readonly Location[],
  known: Map<LocKey, TargetZone>,
): LocKey[] {
  const seen = new Set<LocKey>([MONDIAL]);
  const result: LocKey[] = [MONDIAL];

  for (const f of fs) {
    for (const q of f.rows) {
      if (q.status !== 'active') continue;
      const k = coverageLocKey(q, locations, known);
      if (!seen.has(k)) {
        seen.add(k);
        result.push(k);
      }
    }
  }

  return result;
}

/**
 * Distinct locKeyOf values of the active rows whose coverageLocKey equals `col`.
 * Used by fromCell's zone facet so the list shows exactly the cell's rows.
 */
export function covMembers(
  fs: Family[],
  col: LocKey,
  locations: readonly Location[],
  known: Map<LocKey, TargetZone>,
): LocKey[] {
  const memberSet = new Set<LocKey>();
  for (const f of fs) {
    for (const q of f.rows) {
      if (q.status !== 'active') continue;
      if (coverageLocKey(q, locations, known) === col) {
        memberSet.add(locKeyOf(q, locations));
      }
    }
  }
  return Array.from(memberSet);
}

// ---------------------------------------------------------------------------
// CovCount / CovConversation / coverageMatrix
// ---------------------------------------------------------------------------

/** Prompt count + conversation count for a cell. A 0 member is not written. */
export interface CovCount {
  p: number;   // prompts
  c: number;   // conversations
}

/**
 * Lot I's conversation adapter ([] until I ships).
 * ax is any of the four axes; locs = promptPlaceKey of each conversation zone
 * ('∅' for Mondial), never a zoneKeyFrom / cellKey.
 */
export interface CovConversation {
  ax: BrandQueryAxis;
  locs: LocKey[];
  terms: string[];
}

/**
 * Build the Couverture matrix.
 *
 * null = non mesuré (p = c = 0), NEVER 0.
 * Pending, unclassified, disabled and paused rows never counted.
 * A conversation counts in its axis, at each of its locs, on each term
 * (none → (aucun)).
 */
export function coverageMatrix(
  fs: Family[],
  convs: CovConversation[],
  ax: BrandQueryAxis,
  vocab: string[],
  locs: LocKey[],
  zr: boolean,
  locations: readonly Location[] = [],
  known: Map<LocKey, TargetZone> = new Map(),
): {
  rowKeys: (LocKey | string | null)[];
  colKeys: (LocKey | string | null)[];
  cells: (CovCount | null)[][];
  totals: (CovCount | null)[];
} {
  // Orientation: zr → rows = locs, cols = vocab; else rows = vocab, cols = locs
  const rowKeys: (LocKey | string | null)[] = zr ? [...locs] : [...vocab, null];
  const colKeys: (LocKey | string | null)[] = zr ? [...vocab, null] : [...locs];

  const rowIndex = new Map<string | null, number>();
  rowKeys.forEach((k, i) => rowIndex.set(k, i));
  const colIndex = new Map<string | null, number>();
  colKeys.forEach((k, i) => colIndex.set(k, i));

  const nRows = rowKeys.length;
  const nCols = colKeys.length;
  const cells: (CovCount | null)[][] = Array.from({ length: nRows }, () =>
    Array(nCols).fill(null),
  );

  const bump = (rowKey: string | null, colKey: string | null, dp: number, dc: number) => {
    const ri = rowIndex.get(rowKey);
    const ci = colIndex.get(colKey);
    if (ri === undefined || ci === undefined) return;
    const cur = cells[ri][ci] ?? { p: 0, c: 0 };
    cells[ri][ci] = { p: cur.p + dp, c: cur.c + dc };
  };

  // Prompt rows
  for (const f of fs) {
    for (const q of f.rows) {
      if (q.status !== 'active') continue;
      if (q.axis !== ax) continue;
      const terms = termsOf(q, vocab);
      if (terms === null) continue;  // unclassified — excluded from lanes

      // The SAME key as the lanes (coverageLocs): a Location-derived place needs the brand Locations.
      const locK = coverageLocKey(q, locations, known);
      const termList = terms.length > 0 ? terms : [null as string | null];

      for (const t of termList) {
        if (zr) {
          bump(locK, t, 1, 0);
        } else {
          bump(t, locK, 1, 0);
        }
      }
    }
  }

  // Conversations (lot I; empty until I ships)
  for (const conv of convs) {
    if (conv.ax !== ax) continue;
    const termList = conv.terms.length > 0 ? conv.terms : [null as string | null];
    for (const loc of conv.locs) {
      for (const t of termList) {
        if (zr) {
          bump(loc, t, 0, 1);
        } else {
          bump(t, loc, 0, 1);
        }
      }
    }
  }

  // Row totals (sum over cols)
  const totals: (CovCount | null)[] = rowKeys.map((_, ri) => {
    let p = 0, c = 0;
    for (let ci = 0; ci < nCols; ci++) {
      const cell = cells[ri][ci];
      if (cell) { p += cell.p; c += cell.c; }
    }
    return (p + c) > 0 ? { p, c } : null;
  });

  return { rowKeys, colKeys, cells, totals };
}

// ---------------------------------------------------------------------------
// covShade
// ---------------------------------------------------------------------------

/**
 * UI-SPEC §5.1 fixed violet ramp d1..d5 for a cell value v = p + c.
 */
export function covShade(v: number): { bg: string; fg: string } {
  if (v <= 0)  return { bg: '#f3f0ff', fg: '#5b21b6' };  // d1 (lightest)
  if (v <= 2)  return { bg: '#ddd6fe', fg: '#4c1d95' };  // d2
  if (v <= 5)  return { bg: '#a78bfa', fg: '#1e1b4b' };  // d3
  if (v <= 10) return { bg: '#7c3aed', fg: '#ffffff' };  // d4
  return           { bg: '#4c1d95', fg: '#ffffff' };     // d5 (darkest)
}

// ---------------------------------------------------------------------------
// cellTriple / COV_HATCH_SX
// ---------------------------------------------------------------------------

/**
 * Build a CellTriple from matrix coordinates.
 * zr: rows=locs, cols=vocab → (loc=rowKey, ax, tp=colKey)
 *  !zr: rows=vocab, cols=locs → (loc=colKey, ax, tp=rowKey)
 */
export function cellTriple(
  zr: boolean,
  rowKey: unknown,
  colKey: unknown,
  ax: BrandQueryAxis,
): CellTriple {
  if (zr) {
    return {
      loc: (rowKey as LocKey | null) ?? MONDIAL,
      ax,
      tp: (colKey as string | null),
    };
  }
  return {
    loc: (colKey as LocKey | null) ?? MONDIAL,
    ax,
    tp: (rowKey as string | null),
  };
}

/**
 * MUI `sx` object for the hatched « non mesuré » cell pattern (UI-SPEC §5.3).
 * Define COV_HATCH_SX here; the HATCH_SX in the rest of the codebase is a
 * different pattern.
 */
export const COV_HATCH_SX = {
  backgroundImage: 'repeating-linear-gradient(135deg, #e8e4ff 0px, #e8e4ff 2px, transparent 2px, transparent 8px)',
  backgroundColor: '#f3f0ff',
} as const;
